// Voice guidance for low-literacy users (proposal Section 05/06). In a browser this uses the platform
// TTS; on the Model H the Android shell speaks through the native engine with an Urdu voice (Android
// WebView has no Web Speech API). If no Urdu voice exists, guidance stays on screen.

import { bridgeCall, shellCaps } from './devices.ts';

let enabled = true;

export function setVoice(on: boolean) {
  enabled = on;
  if (!on) stopSpeaking();
}

export const voiceEnabled = () => enabled;

function voiceFor(lang: 'ur' | 'en'): SpeechSynthesisVoice | undefined {
  if (typeof speechSynthesis === 'undefined') return undefined;
  const voices = speechSynthesis.getVoices();
  return lang === 'ur' ? voices.find((v) => v.lang.toLowerCase().startsWith('ur')) : voices.find((v) => v.lang.toLowerCase().startsWith('en'));
}

export function hasVoice(lang: 'ur' | 'en') {
  const caps = shellCaps();
  if (caps) return caps.tts.includes(lang);
  return Boolean(voiceFor(lang));
}

export function speak(text: string, lang: 'ur' | 'en') {
  if (!enabled) return;
  const caps = shellCaps();
  if (caps) {
    // The shell's TTS engine may finish loading after the capability report, so let it decide.
    void bridgeCall('tts.speak', { text, lang }, 5000).catch(() => {});
    return;
  }
  if (typeof speechSynthesis === 'undefined') return;
  const voice = voiceFor(lang);
  if (!voice) return;
  speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.voice = voice;
  u.lang = voice.lang;
  u.rate = 0.9;
  speechSynthesis.speak(u);
}

export function stopSpeaking() {
  if (shellCaps()) {
    void bridgeCall('tts.stop', {}, 2000).catch(() => {});
    return;
  }
  if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
}

// Speech-to-text for symptom intake, where the platform supports Urdu recognition.
type Recognition = { lang: string; interimResults: boolean; onresult: (e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void; onend: () => void; onerror: () => void; start(): void; stop(): void };

/** Recogniser backed by the Android shell's SpeechRecognizer, with the same shape as the Web Speech API. */
function nativeRecognizer(lang: 'ur' | 'en'): Recognition {
  const r: Recognition = {
    lang: lang === 'ur' ? 'ur-PK' : 'en-US',
    interimResults: false,
    onresult: () => {},
    onend: () => {},
    onerror: () => {},
    start() {
      bridgeCall<string>('stt.listen', { lang: r.lang }, 30000)
        .then((transcript) => {
          if (transcript) r.onresult({ results: [[{ transcript }]] });
          r.onend();
        })
        .catch(() => { r.onerror(); r.onend(); });
    },
    stop() {
      void bridgeCall('stt.stop', {}, 2000).catch(() => {});
    },
  };
  return r;
}

export function createRecognizer(lang: 'ur' | 'en'): Recognition | null {
  const caps = shellCaps();
  if (caps) return caps.stt ? nativeRecognizer(lang) : null;
  const Ctor = (window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition }).SpeechRecognition
    ?? (window as unknown as { webkitSpeechRecognition?: new () => Recognition }).webkitSpeechRecognition;
  if (!Ctor) return null;
  const r = new Ctor();
  r.lang = lang === 'ur' ? 'ur-PK' : 'en-US';
  r.interimResults = false;
  return r;
}
